import { type Metadata, Status } from '@subaio/api-types/budget'
import type { UserBank } from '@subaio/api-types/psd2'
import { type BoundHandlers, stepMetaHistoryClear } from '@subaio/components'
import { type Api, logError, tokenDecode } from '@subaio/utils'

import type { BrandFeatures } from 'apiTypes/BrandFeatures'
import type { User } from 'apiTypes/User'
import type { UserLogin } from 'apiTypes/UserLogin'

import type { BrandMetadata } from 'server/brandMetadata'

import { type Step, StepType } from './Step'

export enum UserType {
	Default = 'Default',
	Coapplicant = 'Coapplicant',
	PrimaryWithCoapplicant = 'PrimaryWithCoapplicant',
}

export type UserMetadata = {
	advisorName: string
	applicantName: string
	coapplicantName: string | null
	isPrimary: boolean
	/** The name of the currently logged in user. */
	userName: string
	userType: UserType
}

type Options = {
	api: Api
	sessionId: string

	onLogEvent: (log: string) => void
	setUser: (props: User) => void
}

export const getStepHandlers = ({ sessionId, api, onLogEvent, setUser }: Options) => {
	const handlers = {
		onInitial: async () => {
			// Not strictly necessary, maybe a better flow for unauthorized makes more sense
			if (!/^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/.exec(sessionId)) {
				return { type: StepType.Unauthorized } satisfies Step
			}

			// TODO: We might want to consider adding some way for this app to know if our API is disabled for whatever reason
			// return { type: StepType.Disabled }

			return { type: StepType.Authentication } satisfies Step
		},

		onAuthenticationSubmit: async (code: string) => {
			const loginResponse = await api.post<UserLogin | { invalidReason: 'EXPIRED' | 'INVALID' } | null>(
				'/users/login',
				{ sessionId, code },
			)
			if (loginResponse) {
				if ('invalidReason' in loginResponse) {
					if (loginResponse.invalidReason === 'INVALID') {
						return { type: StepType.Authentication, isLoginInvalid: true } satisfies Step
					}
					if (loginResponse.invalidReason === 'EXPIRED') {
						return { type: StepType.Expired } satisfies Step
					}
				} else {
					const { exp, sub, brandId } = tokenDecode(loginResponse.token) as {
						exp: number
						sub: string
						brandId: string
					}
					setUser({
						token: loginResponse.token,
						expiration: exp,
						userId: sub,
						brandId,
						budgetId: loginResponse.budgetId,
					})

					const [userMetadata, { status, isBankDataReceived }] = await Promise.all([
						api.get<Metadata>(`/budgets/${loginResponse.budgetId}/metadata`).then(
							(metadata): UserMetadata => ({
								userName: metadata.isPrimary ? metadata.applicant!.name : metadata.coapplicant.name!,
								applicantName: metadata.applicant!.name,
								coapplicantName: metadata.coapplicant?.name ?? null,
								isPrimary: metadata.isPrimary,
								advisorName: metadata.advisor!.name,
								userType: (() => {
									if (metadata.coapplicant.name) {
										return metadata.isPrimary ? UserType.PrimaryWithCoapplicant : UserType.Coapplicant
									}
									return UserType.Default
								})(),
							}),
						),
						api.get<{ status: Status; isBankDataReceived: boolean }>(`/budgets/${loginResponse.budgetId}/status`),
					])

					if (status === Status.MissingCoapplicantData) {
						return {
							type: StepType.MissingCoapplicant,
							userMetadata,
						} satisfies Step
					}

					if (status !== Status.Uninitialized) {
						return {
							type: StepType.BudgetEditForm,
							budgetId: loginResponse.budgetId,
							brandFeatures: loginResponse.brandFeatures,
							userMetadata,
						} satisfies Step
					}

					// If we fail to check if a budget already has selected banks, start from the beginning
					try {
						const selectedBanks = await api.get<Array<UserBank>>('/psd2/selected-banks')

						if (selectedBanks.length > 0) {
							return {
								type: StepType.Accounts,
								budgetId: loginResponse.budgetId,
								brandFeatures: loginResponse.brandFeatures,
								userMetadata,
								isBankDataReceived,
							} satisfies Step
						}
					} catch (error) {
						logError(error)
					}

					return {
						type: StepType.Introduction,
						budgetId: loginResponse.budgetId,
						brandMetadata: loginResponse.brandMetadata,
						brandFeatures: loginResponse.brandFeatures,
						userMetadata,
					} satisfies Step
				}
			}

			throw new Error('Failed to login user')
		},
		onIntroductionNext: (
			budgetId: string,
			brandMetadata: BrandMetadata,
			brandFeatures: Array<BrandFeatures>,
			userMetadata: UserMetadata,
		) =>
			({
				type: StepType.Terms,
				budgetId,
				brandMetadata,
				brandFeatures,
				userMetadata,
			}) satisfies Step,
		onTermsAccept: async (budgetId: string, brandFeatures: Array<BrandFeatures>, userMetadata: UserMetadata) => {
			onLogEvent('TERMS_ACCEPTED')

			return {
				type: StepType.Accounts,
				budgetId,
				brandFeatures,
				userMetadata,
				isBankDataReceived: false,
			} satisfies Step
		},
		onAccountSelectComplete: async (
			budgetId: string,
			brandFeatures: Array<BrandFeatures>,
			userMetadata: UserMetadata,
		) => {
			// If the user is a coapplicant, we skip the budget edit form
			if (userMetadata.userType === UserType.Coapplicant) {
				await api.delete(`/users/${sessionId}`)
				onLogEvent('COAPPLICANT_COMPLETE')

				return stepMetaHistoryClear({
					type: StepType.Success,
					userMetadata,
				} satisfies Step)
			}

			const { status } = await api.get<{ status: Status }>(`/budgets/${budgetId}/status`)

			if (status === Status.MissingCoapplicantData) {
				return {
					type: StepType.MissingCoapplicant,
					userMetadata,
				} satisfies Step
			}

			return {
				type: StepType.BudgetEditForm,
				budgetId,
				brandFeatures,
				userMetadata,
			} satisfies Step
		},
		onBudgetEditFinish: async (budgetId: string, userMetadata: UserMetadata) => {
			await api.post(`/budgets/${budgetId}/completions`)
			await api.delete(`/users/${sessionId}`)
			onLogEvent('BUDGET_COMPLETE')

			return stepMetaHistoryClear({
				type: StepType.Success,
				userMetadata,
			} satisfies Step)
		},
	}

	return handlers
}

export type StepHandlers = BoundHandlers<Step, ReturnType<typeof getStepHandlers>>
